import { createPanelTemplate } from "../comic/PanelTemplates";
import { clampBubbleToPanel } from "../comic/BubbleGeometry";
import { sanitizeBubbleText } from "../comic/BubbleTextLayout";
import {
  COMIC_DOCUMENT_SCHEMA_VERSION,
  MAX_BUBBLE_TEXT_LENGTH,
  MAX_COMIC_OBJECTS,
  isComicBubbleType,
  isComicTemplateId,
  type ComicBubbleObject,
  type ComicTemplateId,
  type PersistedDocumentState,
} from "../comic/comicTypes";
import {
  MAX_PROJECT_NAME_LENGTH,
  PROJECT_SCHEMA_VERSION,
  sanitizeProjectName,
} from "../projects/createProject";
import type {
  PerfectDrawProject,
  ProjectOrientation,
  ProjectSettings,
  ProjectType,
} from "../projects/types";
import { readPngDimensions } from "../persistence/artworkCodec";
import {
  MAX_IMPORTED_NONBLANK_LAYER_PIXELS,
  MAX_RASTER_LAYERS,
  type PersistedLayerState,
  type StoredRasterLayerBlob,
} from "../layers/layerTypes";
import { sanitizeLayerName } from "../layers/layerNames";
import { validateLayerDocument } from "../layers/layerValidation";
import { assertAggregateRasterMemoryWithinBudget } from "../layers/layerMemory";

export const BACKUP_FORMAT = "perfect-draw-project";
export const BACKUP_FORMAT_VERSION = 3;
export const BACKUP_MIME_TYPE = "application/vnd.perfectdraw+json";
export const MAX_ARTWORK_BYTES = 20 * 1024 * 1024;
export const MAX_BACKUP_JSON_OVERHEAD_BYTES = 4 * 1024 * 1024;
export const MAX_BACKUP_ENCODED_ARTWORK_BYTES =
  4 * (Math.ceil(MAX_ARTWORK_BYTES / 3) + MAX_RASTER_LAYERS - 1);
// The importer accepts every export whose decoded PNG total is valid: base64
// has an exact 4/3 ceiling and the bounded PERFECT DRAW envelope gets separate
// headroom. Export still validates its exact serialized byte length pre-download.
export const MAX_BACKUP_FILE_BYTES =
  MAX_BACKUP_ENCODED_ARTWORK_BYTES + MAX_BACKUP_JSON_OVERHEAD_BYTES;
export const MAX_IMPORT_DIMENSION = 4096;
export const MAX_IMPORT_PIXELS = 16_000_000;

export interface SafeProjectMetadata {
  name: string;
  type: ProjectType;
  orientation: ProjectOrientation;
  width: number;
  height: number;
  settings?: Partial<ProjectSettings>;
}

export interface SafeComicBackup {
  templateId: ComicTemplateId;
  dialogueLayerId: string;
  panels: Array<{ id: string; templateSlot: number }>;
  objects: ComicBubbleObject[];
}

interface BackupArtwork {
  mimeType: "image/png";
  base64: string;
}

export interface PerfectDrawBackupV1 {
  format: typeof BACKUP_FORMAT;
  formatVersion: 1;
  exportedAt: string;
  project: SafeProjectMetadata;
  artwork: BackupArtwork;
}

export interface PerfectDrawBackupV2 {
  format: typeof BACKUP_FORMAT;
  formatVersion: 2;
  exportedAt: string;
  project: SafeProjectMetadata;
  comic?: SafeComicBackup;
  artwork: BackupArtwork;
}

export interface PerfectDrawBackupV3 {
  format: typeof BACKUP_FORMAT;
  formatVersion: typeof BACKUP_FORMAT_VERSION;
  exportedAt: string;
  project: SafeProjectMetadata;
  activeLayerId: string;
  layers: PersistedLayerState[];
  comic?: SafeComicBackup;
  artwork: {
    layers: Array<{
      layerId: string;
      mimeType: "image/png";
      blank: boolean;
      base64?: string;
    }>;
  };
}

export type PerfectDrawBackup = PerfectDrawBackupV1 | PerfectDrawBackupV2 | PerfectDrawBackupV3;

interface ValidatedComicBackup {
  templateId: ComicTemplateId;
  panelSlotsBySourceId: Map<string, number>;
  sourceDialogueLayerId: string;
  objects: ComicBubbleObject[];
}

export interface ValidatedBackup {
  formatVersion: 1 | 2 | 3;
  project: SafeProjectMetadata;
  artworkBytes: Uint8Array;
  layers?: PersistedLayerState[];
  activeLayerId?: string;
  rasterArtwork?: Array<{ sourceLayerId: string; bytes?: Uint8Array; blank: boolean }>;
  comic?: ValidatedComicBackup;
}

export interface ImportedProjectData {
  project: PerfectDrawProject;
  documentState: PersistedDocumentState;
  rasterLayers: Array<StoredRasterLayerBlob & { sourceBytes?: Uint8Array }>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isProjectType(value: unknown): value is ProjectType {
  return value === "drawing" || value === "comic";
}

function isOrientation(value: unknown): value is ProjectOrientation {
  return value === "portrait" || value === "landscape" || value === "square";
}

function finite(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function decodeBase64(value: string): Uint8Array {
  if (value.length === 0 || value.length % 4 !== 0 || !/^[A-Za-z0-9+/]*={0,2}$/.test(value)) {
    throw new Error("Invalid artwork encoding.");
  }
  let binary: string;
  try {
    binary = atob(value);
  } catch {
    throw new Error("Invalid artwork encoding.");
  }
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  if (bytes.byteLength > MAX_ARTWORK_BYTES) throw new Error("The artwork is too large.");
  return bytes;
}

function validateProjectMetadata(rawProject: Record<string, unknown>): SafeProjectMetadata {
  if (
    typeof rawProject.name !== "string" ||
    rawProject.name.length === 0 ||
    rawProject.name.length > MAX_PROJECT_NAME_LENGTH
  ) {
    throw new Error("The project name is invalid.");
  }
  if (!isProjectType(rawProject.type) || !isOrientation(rawProject.orientation)) {
    throw new Error("The project type or orientation is invalid.");
  }
  if (
    !Number.isInteger(rawProject.width) ||
    !Number.isInteger(rawProject.height) ||
    (rawProject.width as number) < 1 ||
    (rawProject.height as number) < 1 ||
    (rawProject.width as number) > MAX_IMPORT_DIMENSION ||
    (rawProject.height as number) > MAX_IMPORT_DIMENSION
  ) {
    throw new Error("The project dimensions are invalid.");
  }
  const width = rawProject.width as number;
  const height = rawProject.height as number;
  if (width * height > MAX_IMPORT_PIXELS) throw new Error("The project has too many pixels.");
  const settings = isRecord(rawProject.settings)
    ? {
        backgroundColor:
          typeof rawProject.settings.backgroundColor === "string" &&
          /^#[0-9a-fA-F]{6}$/.test(rawProject.settings.backgroundColor)
            ? rawProject.settings.backgroundColor
            : undefined,
        easyMode:
          typeof rawProject.settings.easyMode === "boolean"
            ? rawProject.settings.easyMode
            : undefined,
        pressureEnabled:
          typeof rawProject.settings.pressureEnabled === "boolean"
            ? rawProject.settings.pressureEnabled
            : undefined,
        autosaveEnabled:
          typeof rawProject.settings.autosaveEnabled === "boolean"
            ? rawProject.settings.autosaveEnabled
            : undefined,
      }
    : undefined;
  return {
    name: rawProject.name,
    type: rawProject.type,
    orientation: rawProject.orientation,
    width,
    height,
    settings,
  };
}

function validateComicBackup(value: unknown, metadata: SafeProjectMetadata): ValidatedComicBackup {
  if (!isRecord(value) || !isComicTemplateId(value.templateId)) {
    throw new Error("The comic template is invalid.");
  }
  if (typeof value.dialogueLayerId !== "string" || value.dialogueLayerId.length === 0) {
    throw new Error("The comic layer reference is invalid.");
  }
  if (!Array.isArray(value.panels) || !Array.isArray(value.objects)) {
    throw new Error("The comic backup structure is incomplete.");
  }
  if (value.objects.length > MAX_COMIC_OBJECTS) throw new Error("The comic has too many objects.");
  const expectedPanels = createPanelTemplate(
    value.templateId,
    metadata.width,
    metadata.height,
    metadata.orientation,
    (slot) => `validated-panel-${slot}`,
  );
  if (value.panels.length !== expectedPanels.length) throw new Error("Comic panels are invalid.");
  const panelSlotsBySourceId = new Map<string, number>();
  const usedSlots = new Set<number>();
  for (const panel of value.panels) {
    if (
      !isRecord(panel) ||
      typeof panel.id !== "string" ||
      !Number.isInteger(panel.templateSlot) ||
      (panel.templateSlot as number) < 0 ||
      (panel.templateSlot as number) >= expectedPanels.length ||
      panelSlotsBySourceId.has(panel.id) ||
      usedSlots.has(panel.templateSlot as number)
    ) {
      throw new Error("Comic panels are invalid.");
    }
    panelSlotsBySourceId.set(panel.id, panel.templateSlot as number);
    usedSlots.add(panel.templateSlot as number);
  }

  const objectIds = new Set<string>();
  const objects = value.objects.map((raw): ComicBubbleObject => {
    if (
      !isRecord(raw) ||
      typeof raw.id !== "string" ||
      objectIds.has(raw.id) ||
      !isComicBubbleType(raw.type) ||
      typeof raw.panelId !== "string" ||
      !panelSlotsBySourceId.has(raw.panelId) ||
      raw.layerId !== value.dialogueLayerId ||
      !finite(raw.x) ||
      !finite(raw.y) ||
      !finite(raw.width) ||
      !finite(raw.height) ||
      !finite(raw.rotation) ||
      raw.rotation < -180 ||
      raw.rotation > 180 ||
      !finite(raw.order) ||
      raw.width <= 0 ||
      raw.height <= 0 ||
      !isRecord(raw.data)
    ) {
      throw new Error("A comic object is invalid.");
    }
    objectIds.add(raw.id);
    if (
      typeof raw.data.text !== "string" ||
      raw.data.text.length > MAX_BUBBLE_TEXT_LENGTH ||
      !finite(raw.data.fontSize) ||
      raw.data.fontSize < 12 ||
      raw.data.fontSize > 160 ||
      (raw.data.textAlign !== "left" && raw.data.textAlign !== "center") ||
      typeof raw.data.fillColor !== "string" ||
      !/^#[0-9a-fA-F]{6}$/.test(raw.data.fillColor) ||
      typeof raw.data.strokeColor !== "string" ||
      !/^#[0-9a-fA-F]{6}$/.test(raw.data.strokeColor) ||
      !finite(raw.data.strokeWidth) ||
      raw.data.strokeWidth < 0 ||
      raw.data.strokeWidth > 24
    ) {
      throw new Error("A comic object style is invalid.");
    }
    const slot = panelSlotsBySourceId.get(raw.panelId)!;
    const panel = expectedPanels[slot]!;
    const object: ComicBubbleObject = {
      id: raw.id,
      type: raw.type,
      panelId: raw.panelId,
      layerId: raw.layerId as string,
      x: raw.x,
      y: raw.y,
      width: raw.width,
      height: raw.height,
      rotation: raw.rotation,
      order: raw.order,
      data: {
        text: sanitizeBubbleText(raw.data.text),
        fontSize: raw.data.fontSize,
        textAlign: raw.data.textAlign,
        fillColor: raw.data.fillColor.toLowerCase(),
        strokeColor: raw.data.strokeColor.toLowerCase(),
        strokeWidth: raw.data.strokeWidth,
        tailDirection:
          raw.data.tailDirection === "bottom-left" ||
          raw.data.tailDirection === "bottom-right" ||
          raw.data.tailDirection === "left" ||
          raw.data.tailDirection === "right"
            ? raw.data.tailDirection
            : undefined,
      },
    };
    const clamped = clampBubbleToPanel(object, panel);
    if (
      Math.abs(clamped.x - object.x) > 0.01 ||
      Math.abs(clamped.y - object.y) > 0.01 ||
      Math.abs(clamped.width - object.width) > 0.01 ||
      Math.abs(clamped.height - object.height) > 0.01 ||
      Math.abs(clamped.rotation - object.rotation) > 0.01 ||
      Math.abs(clamped.data.strokeWidth - object.data.strokeWidth) > 0.01
    ) {
      throw new Error("A comic object lies outside its panel.");
    }
    return object;
  });
  return {
    templateId: value.templateId,
    panelSlotsBySourceId,
    sourceDialogueLayerId: value.dialogueLayerId,
    objects,
  };
}

function validateV3Layers(
  parsed: Record<string, unknown>,
  project: SafeProjectMetadata,
): {
  layers: PersistedLayerState[];
  activeLayerId: string;
  rasterArtwork: Array<{ sourceLayerId: string; bytes?: Uint8Array; blank: boolean }>;
} {
  if (!Array.isArray(parsed.layers) || typeof parsed.activeLayerId !== "string") {
    throw new Error("The layer stack is incomplete.");
  }
  for (const layer of parsed.layers) {
    if (
      !isRecord(layer) ||
      typeof layer.name !== "string" ||
      layer.name.length > 40 ||
      !sanitizeLayerName(layer.name)
    ) {
      throw new Error("A layer name is invalid.");
    }
  }
  const validated = validateLayerDocument(parsed.layers, parsed.activeLayerId, project.type);
  if (!isRecord(parsed.artwork) || !Array.isArray(parsed.artwork.layers)) {
    throw new Error("The layer artwork is incomplete.");
  }
  const rasterIds = validated.layers
    .filter((layer) => layer.type === "raster")
    .map((layer) => layer.id);
  if (parsed.artwork.layers.length !== rasterIds.length) {
    throw new Error("The layer artwork is incomplete.");
  }
  const seen = new Set<string>();
  const encodedLayers = parsed.artwork.layers.map((raw) => {
    if (
      !isRecord(raw) ||
      typeof raw.layerId !== "string" ||
      !rasterIds.includes(raw.layerId) ||
      seen.has(raw.layerId) ||
      raw.mimeType !== "image/png" ||
      typeof raw.blank !== "boolean"
    ) {
      throw new Error("A raster layer entry is invalid.");
    }
    seen.add(raw.layerId);
    if (raw.blank) {
      if (raw.base64 !== undefined) throw new Error("A blank layer cannot contain artwork.");
      return { sourceLayerId: raw.layerId, blank: true, base64: undefined };
    }
    if (typeof raw.base64 !== "string") throw new Error("Layer artwork is missing.");
    return { sourceLayerId: raw.layerId, blank: false, base64: raw.base64 };
  });
  const nonblankPixels =
    encodedLayers.filter((layer) => !layer.blank).length * project.width * project.height;
  if (nonblankPixels > MAX_IMPORTED_NONBLANK_LAYER_PIXELS) {
    throw new Error("The backup contains too many nonblank layer pixels.");
  }
  assertAggregateRasterMemoryWithinBudget(project.width, project.height, encodedLayers);

  let decodedTotal = 0;
  const rasterArtwork = encodedLayers.map((layer) => {
    if (layer.blank) return { sourceLayerId: layer.sourceLayerId, blank: true };
    const bytes = decodeBase64(layer.base64!);
    decodedTotal += bytes.byteLength;
    if (decodedTotal > MAX_ARTWORK_BYTES) throw new Error("The layer artwork is too large.");
    const dimensions = readPngDimensions(bytes);
    if (dimensions.width !== project.width || dimensions.height !== project.height) {
      throw new Error("Layer artwork dimensions do not match the project.");
    }
    return { sourceLayerId: layer.sourceLayerId, blank: false, bytes };
  });
  return { layers: validated.layers, activeLayerId: validated.activeLayerId, rasterArtwork };
}

export function validateBackupText(text: string, fileSize: number): ValidatedBackup {
  if (fileSize > MAX_BACKUP_FILE_BYTES) throw new Error("The backup file is too large.");
  let parsed: unknown;
  try {
    parsed = JSON.parse(text) as unknown;
  } catch {
    throw new Error("The backup is not valid JSON.");
  }
  if (!isRecord(parsed) || parsed.format !== BACKUP_FORMAT) {
    throw new Error("This is not a PERFECT DRAW backup.");
  }
  if (
    parsed.formatVersion !== 1 &&
    parsed.formatVersion !== 2 &&
    parsed.formatVersion !== BACKUP_FORMAT_VERSION
  ) {
    throw new Error("This backup version is not supported.");
  }
  if (!isRecord(parsed.project) || !isRecord(parsed.artwork)) {
    throw new Error("The backup structure is incomplete.");
  }
  const project = validateProjectMetadata(parsed.project);
  if (parsed.formatVersion === 3) {
    const layerData = validateV3Layers(parsed, project);
    const comic = project.type === "comic" ? validateComicBackup(parsed.comic, project) : undefined;
    if (project.type === "drawing" && parsed.comic !== undefined) {
      throw new Error("A drawing backup cannot contain comic data.");
    }
    const dialogueLayerId = layerData.layers.find((layer) => layer.type === "object")?.id;
    if (comic && comic.sourceDialogueLayerId !== dialogueLayerId) {
      throw new Error("Comic objects do not reference the Dialogue layer.");
    }
    return {
      formatVersion: 3,
      project,
      artworkBytes: new Uint8Array(),
      layers: layerData.layers,
      activeLayerId: layerData.activeLayerId,
      rasterArtwork: layerData.rasterArtwork,
      comic,
    };
  }
  if (parsed.artwork.mimeType !== "image/png" || typeof parsed.artwork.base64 !== "string") {
    throw new Error("The artwork format is invalid.");
  }
  const artworkBytes = decodeBase64(parsed.artwork.base64);
  assertAggregateRasterMemoryWithinBudget(project.width, project.height, [{ blank: false }]);
  const imageDimensions = readPngDimensions(artworkBytes);
  if (imageDimensions.width !== project.width || imageDimensions.height !== project.height) {
    throw new Error("The artwork dimensions do not match the project.");
  }
  const comic =
    parsed.formatVersion === 2 && project.type === "comic"
      ? validateComicBackup(parsed.comic, project)
      : undefined;
  if (parsed.formatVersion === 2 && project.type === "drawing" && parsed.comic !== undefined) {
    throw new Error("A drawing backup cannot contain comic data.");
  }
  return { formatVersion: parsed.formatVersion, project, artworkBytes, comic };
}

export function createImportedProjectData(validated: ValidatedBackup): ImportedProjectData {
  const metadata = validated.project;
  const timestamp = new Date().toISOString();
  const projectId = crypto.randomUUID();
  const sourceLayers = validated.layers;
  const layerIdMap = new Map<string, string>();
  if (sourceLayers) {
    for (const layer of sourceLayers) layerIdMap.set(layer.id, crypto.randomUUID());
  }
  const rasterLayerId = sourceLayers
    ? layerIdMap.get(sourceLayers.find((layer) => layer.type === "raster")!.id)!
    : crypto.randomUUID();
  const dialogueLayerId = sourceLayers
    ? layerIdMap.get(sourceLayers.find((layer) => layer.type === "object")?.id ?? "")
    : metadata.type === "comic"
      ? crypto.randomUUID()
      : undefined;
  const templateId =
    metadata.type === "comic" ? (validated.comic?.templateId ?? "one-panel") : undefined;
  const panels = templateId
    ? createPanelTemplate(templateId, metadata.width, metadata.height, metadata.orientation, () =>
        crypto.randomUUID(),
      )
    : [];
  const objects =
    validated.comic && dialogueLayerId
      ? validated.comic.objects.map((source) => {
          const slot = validated.comic!.panelSlotsBySourceId.get(source.panelId)!;
          const panel = panels.find((candidate) => candidate.templateSlot === slot)!;
          return clampBubbleToPanel(
            {
              ...source,
              id: crypto.randomUUID(),
              panelId: panel.id,
              layerId: dialogueLayerId,
              data: { ...source.data },
            },
            panel,
          );
        })
      : [];
  const project: PerfectDrawProject = {
    schemaVersion: PROJECT_SCHEMA_VERSION,
    id: projectId,
    name: sanitizeProjectName(metadata.name, metadata.type),
    type: metadata.type,
    createdAt: timestamp,
    updatedAt: timestamp,
    width: metadata.width,
    height: metadata.height,
    orientation: metadata.orientation,
    comicTemplateId: templateId,
    activeLayerId:
      (validated.activeLayerId && layerIdMap.get(validated.activeLayerId)) ?? rasterLayerId,
    layers: sourceLayers
      ? sourceLayers.map((layer) => ({ ...layer, id: layerIdMap.get(layer.id)! }))
      : [
          {
            id: rasterLayerId,
            name: "Sketch",
            type: "raster" as const,
            role: "sketch" as const,
            system: false,
            visible: true,
            locked: false,
            opacity: 1,
            order: 0,
          },
          ...(dialogueLayerId
            ? [
                {
                  id: dialogueLayerId,
                  name: "Dialogue",
                  type: "object" as const,
                  role: "dialogue" as const,
                  system: true,
                  visible: true,
                  locked: false,
                  opacity: 1,
                  order: 1,
                },
              ]
            : []),
        ],
    panels,
    objects,
    settings: {
      backgroundColor: metadata.settings?.backgroundColor ?? "#ffffff",
      easyMode: metadata.settings?.easyMode ?? true,
      pressureEnabled: metadata.settings?.pressureEnabled ?? true,
      autosaveEnabled: metadata.settings?.autosaveEnabled ?? true,
    },
  };
  return {
    project,
    documentState: {
      schemaVersion: COMIC_DOCUMENT_SCHEMA_VERSION,
      activeLayerId: project.activeLayerId,
      layers: project.layers.map((layer) => ({ ...layer })),
      comicTemplateId: templateId,
      panels,
      objects,
    },
    rasterLayers: validated.rasterArtwork
      ? validated.rasterArtwork.map((entry) => ({
          layerId: layerIdMap.get(entry.sourceLayerId)!,
          mimeType: "image/png" as const,
          blank: entry.blank,
          blob: entry.bytes
            ? new Blob([new Uint8Array(entry.bytes).buffer], { type: "image/png" })
            : undefined,
          sourceBytes: entry.bytes,
        }))
      : [
          {
            layerId: rasterLayerId,
            mimeType: "image/png" as const,
            blank: validated.artworkBytes.byteLength === 0,
            blob:
              validated.artworkBytes.byteLength > 0
                ? new Blob([new Uint8Array(validated.artworkBytes).buffer], { type: "image/png" })
                : undefined,
            sourceBytes: validated.artworkBytes,
          },
        ],
  };
}

export function createImportedProject(metadata: SafeProjectMetadata): PerfectDrawProject {
  return createImportedProjectData({
    formatVersion: 1,
    project: metadata,
    artworkBytes: new Uint8Array(),
  }).project;
}
